import store from '@/store'

/**
 * Token Refresh Manager
 * Monitors JWT expiration and automatically refreshes tokens for providers that support it (e.g., LDAP)
 */
class TokenRefreshManager {
    constructor() {
        this.refreshTimer = null
        this.refreshMarginSeconds = 300 // Refresh 5 minutes before expiration
        this.isRefreshing = false
    }

    /**
     * Parse JWT and extract expiration time
     * @param {string} token - JWT token
     * @returns {number|null} Expiration timestamp in milliseconds, or null if invalid
     */
    parseTokenExpiration(token) {
        if (!token || token === 'undefined') return null

        try {
            const payload = JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString())
            return payload.exp ? payload.exp * 1000 : null // Convert to milliseconds
        } catch (e) {
            console.error('Failed to parse JWT:', e)
            return null
        }
    }

    /**
     * Calculate time until token should be refreshed (in milliseconds)
     * @param {number} expirationTime - Token expiration timestamp in milliseconds
     * @returns {number} Milliseconds until refresh, or 0 if already expired
     */
    calculateRefreshDelay(expirationTime) {
        const now = Date.now()
        const refreshTime = expirationTime - (this.refreshMarginSeconds * 1000)
        const delay = refreshTime - now
        return delay > 0 ? delay : 0
    }

    /**
     * Start monitoring token expiration
     */
    start() {
        this.stop() // Clear any existing timer

        const state = store.state.login

        // Only monitor if user is authenticated and using a provider that supports refresh
        if (!state.user || !state.authMethod) {
            return
        }

        // Check if the current auth provider supports token refresh
        const AuthProviderFactory = require('@/domain/login/providers/AuthProviderFactory').default
        const provider = AuthProviderFactory.getProvider(state.authMethod)

        if (!provider.supportsRefresh())  return

        // Get token expiration
        const token = state.token?.token || state.jwt
        const expirationTime = this.parseTokenExpiration(token)

        if (!expirationTime) {
            console.warn('Cannot schedule token refresh: no valid expiration time')
            return
        }

        const delay = this.calculateRefreshDelay(expirationTime)
        if (delay === 0) {
            // Token already expired or expiring very soon, refresh immediately
            this.refresh()
        } else {
            // Schedule refresh
            this.refreshTimer = setTimeout(() => {
                this.refresh()
            }, delay)
        }
    }

    /**
     * Stop monitoring token expiration
     */
    stop() {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer)
            this.refreshTimer = null
        }
    }

    /**
     * Perform token refresh
     */
    async refresh() {
        if (this.isRefreshing) return

        this.isRefreshing = true

        try {
            await store.dispatch('login/refreshAuthToken')
            // Restart monitoring with new token
            this.start()
        } catch (e) {
            // Handle specific errors
            if (e.data?.code === 'auth.token_expired' ||
                e.data?.code === 'auth.token_theft_detected' ||
                e.data?.code === 'auth.token_revoked') {
                // Token is no longer valid, logout user
                console.warn('Token no longer valid, logging out...')
                await store.dispatch('login/logout')
            } else {
                // Other error, retry in 1 minute
                console.log('Will retry token refresh in 1 minute')
                this.refreshTimer = setTimeout(() => {
                    this.refresh()
                }, 60000)
            }
        } finally {
            this.isRefreshing = false
        }
    }

    /**
     * Manually trigger token refresh (useful for 401 error recovery)
     * @returns {Promise<void>}
     */
    async forceRefresh() {
        return this.refresh()
    }
}

// Export singleton instance
export default new TokenRefreshManager()
